import API from 'core/Api';
import * as ErrorMonitor from 'core/ErrorMonitor';
import { setUserInfo } from './auth';

/**
 * Response type from auth/me endpoint
 */
interface AuthMeResponse {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  work_mode: string;
  work_mode_name: string;
  alt_work_mode_name: string;
  lang: string;
  paid_leave_days: number;
  paid_leave_days_by_year: any[];
  paid_leave_served_time_days: number;
  paid_leave_irregular_working_hours: number;
  paid_leave_irregular_working_hours_days?: number;
  paid_leave_specific_working_conditions: number;
  paid_leave_specific_working_conditions_days?: number;
  paid_leave_additional_days: number;
  wfh_limit_type: string;
  remaining_wfh_leaves_per_week: number;
  remaining_wfh_leaves_per_month: number;
  feature_leaves_is_active: boolean;
  feature_leave_review_is_active: boolean;
  feature_leaves_half_day_option_is_active: boolean;
  feature_form_76_is_active: boolean;
  feature_colleagues_leaves_is_active: boolean;
  feature_exit_interview_is_active: boolean;
  feature_sick_leave_without_attachment_is_active: boolean;
  feature_art157_leave_without_attachment_is_active: boolean;
  feature_leave_days_served_time_is_active: boolean;
  feature_leave_days_irregular_working_hours_is_active: boolean;
  feature_leave_days_specific_working_conditions_is_active: boolean;
  feature_paid_additional_leave_is_active: boolean;
  feature_hourly_absences_is_active: boolean;
  feature_health_and_safety_training_is_active: boolean;
  feature_work_trips_is_active: boolean;
  feature_custom_feature_for_client_f: boolean;
  feature_limit_wfh_leave_days: boolean;
  feature_show_wfh_leave_type_in_main_dropdown: boolean;
  feature_leave_substitute_employee_is_active: boolean;
  feature_require_leave_substitute_employee_is_active: boolean;
  feature_employee_candidates_is_active: boolean;
  feature_group_role_limits_is_active: boolean;
  feature_reminder_for_probation_period_end: boolean;
  feature_limit_documents_to_leaves: boolean;
  reminder_for_id_card_expiration: boolean;
  should_disallow_leave_days: boolean;
  disallowed_leaves_from: string;
  disallowed_leaves_to: string;
  is_candidate: boolean;
  employee_candidate_help_text: string;
  two_fa_type?: boolean;
  total_hourly_absences_remaining_hours?: number;
  total_hourly_absences_uncovered_hours?: number;
  total_hourly_absences_hours_about_to_be_covered?: number;
  has_available_archives?: boolean;
  available_undownloaded_archives_count?: number;
  in_progress_archives_count?: number;
  last_archive_expires_at?: string | null;
}

/**
 * Options for updating user data
 */
interface UpdateUserDataOptions {
  setWorkMode?: (mode: string) => void;
  setWorkModeName?: (name: string) => void;
  setAltWorkModeName?: (name: string) => void;
  setLanguage?: (lang: string) => void;
  onSuccess?: (data: AuthMeResponse) => void;
  onError?: (error: any) => void;
}

/**
 * Centralized function to fetch and update user data from auth/me endpoint
 * This eliminates duplication across multiple components
 *
 * @param options - Optional callbacks for setting context values and handling success/error
 * @returns Promise with the user data
 *
 * @example
 * // Basic usage
 * await updateUserData();
 *
 * @example
 * // With context setters
 * await updateUserData({
 *   setWorkMode,
 *   setWorkModeName,
 *   setAltWorkModeName,
 *   setLanguage,
 * });
 *
 * @example
 * // With success callback for additional processing
 * await updateUserData({
 *   onSuccess: (data) => {
 *     console.log('User data updated', data);
 *   }
 * });
 */
export const updateUserData = async (options: UpdateUserDataOptions = {}): Promise<AuthMeResponse> => {
  const { setWorkMode, setWorkModeName, setAltWorkModeName, setLanguage, onSuccess, onError } = options;

  try {
    const { data } = await API.get<AuthMeResponse>('auth/me');

    const {
      id,
      first_name: firstName,
      last_name: lastName,
      email,
      work_mode: workMode,
      work_mode_name: workModeName,
      alt_work_mode_name: altWorkModeName,
      lang,
      paid_leave_days: paidLeaveDays,
      paid_leave_days_by_year: paidLeaveDaysByYear,
      paid_leave_served_time_days: paidLeaveDaysServedTime,
      paid_leave_irregular_working_hours: paidLeaveDaysIrregularWorkingHours,
      paid_leave_irregular_working_hours_days: paidLeaveDaysIrregularWorkingHoursDays,
      paid_leave_specific_working_conditions: paidLeaveDaysSpecificWorkingConditions,
      paid_leave_specific_working_conditions_days: paidLeaveDaysSpecificWorkingConditionsDays,
      paid_leave_additional_days: paidLeaveDaysAdditionalLeave,
      wfh_limit_type: wfhLimitType,
      remaining_wfh_leaves_per_week: remainingWfhLeavesPerWeek,
      remaining_wfh_leaves_per_month: remainingWfhLeavesPerMonth,
      feature_leaves_is_active: featureLeavesIsActive,
      feature_leave_review_is_active: featureLeaveReview,
      feature_leaves_half_day_option_is_active: featureLeavesHalfDayOption,
      feature_form_76_is_active: featureForm76,
      feature_colleagues_leaves_is_active: featureColleaguesLeaves,
      feature_exit_interview_is_active: featureExitInterview,
      feature_sick_leave_without_attachment_is_active: featureSickLeaveWithoutAttachment,
      feature_art157_leave_without_attachment_is_active: featureArt157LeaveWithoutAttachment,
      feature_leave_days_served_time_is_active: featureLeaveDaysServedTime,
      feature_leave_days_irregular_working_hours_is_active: featureLeaveDaysIrregularWorkingHours,
      feature_leave_days_specific_working_conditions_is_active: featureLeaveDaysSpecificWorkingConditions,
      feature_paid_additional_leave_is_active: featureLeaveDaysAdditionalLeave,
      feature_hourly_absences_is_active: featureHourlyAbsences,
      feature_health_and_safety_training_is_active: featureHealthAndSafetyTraining,
      feature_work_trips_is_active: featureWorkTrips,
      feature_custom_feature_for_client_f: featureCustomFeatureForClientF,
      feature_limit_wfh_leave_days: featureLimitWfhLeaveDays,
      feature_show_wfh_leave_type_in_main_dropdown: featureShowWFHLeaveTypeInMainDropdown,
      feature_leave_substitute_employee_is_active: featureLeaveSubstituteEmployee,
      feature_require_leave_substitute_employee_is_active: featureRequireLeaveSubstituteEmployee,
      feature_employee_candidates_is_active: featureEmployeeCandidatesIsActive,
      feature_group_role_limits_is_active: featureGroupRoleLimitsIsActive,
      feature_reminder_for_probation_period_end: featureReminderForProbationPeriodEnd,
      feature_limit_documents_to_leaves: featureLimitDocumentsToLeaves,
      reminder_for_id_card_expiration: reminderForIdCardExpiration,
      should_disallow_leave_days: shouldDisallowLeaveDays,
      disallowed_leaves_from: disallowedLeavesFrom,
      disallowed_leaves_to: disallowedLeavesTo,
      is_candidate: isCandidate,
      employee_candidate_help_text: employeeCandidateHelpText,
      total_hourly_absences_remaining_hours: totalHourlyAbsencesRemainingHours,
      total_hourly_absences_uncovered_hours: totalHourlyAbsencesUncoveredHours,
      total_hourly_absences_hours_about_to_be_covered: totalHourlyAbsencesHoursAboutToBeCovered,
      has_available_archives: hasAvailableArchives,
      available_undownloaded_archives_count: availableUndownloadedArchivesCount,
      in_progress_archives_count: inProgressArchivesCount,
      last_archive_expires_at: lastArchiveExpiresAt,
    } = data;

    // Set user in error monitoring
    ErrorMonitor.setUser({ id: String(id), email });

    // Update user info in local storage
    setUserInfo({
      id,
      name: `${firstName} ${lastName}`,
      email,
      paidLeaveDays: paidLeaveDays || 0,
      paidLeaveDaysByYear: paidLeaveDaysByYear || [],
      paidLeaveDaysServedTime: paidLeaveDaysServedTime || 0,
      paidLeaveDaysIrregularWorkingHours:
        paidLeaveDaysIrregularWorkingHours || paidLeaveDaysIrregularWorkingHoursDays || 0,
      paidLeaveDaysSpecificWorkingConditions:
        paidLeaveDaysSpecificWorkingConditions || paidLeaveDaysSpecificWorkingConditionsDays || 0,
      paidLeaveDaysAdditionalLeave: paidLeaveDaysAdditionalLeave || 0,
      wfhLimitType,
      remainingWfhLeavesPerWeek: remainingWfhLeavesPerWeek || 0,
      remainingWfhLeavesPerMonth: remainingWfhLeavesPerMonth || 0,
      featureLeavesIsActive,
      featureLeaveReview,
      featureLeavesHalfDayOption,
      featureForm76,
      featureColleaguesLeaves,
      featureExitInterview,
      featureSickLeaveWithoutAttachment,
      featureArt157LeaveWithoutAttachment,
      featureLeaveDaysServedTime,
      featureLeaveDaysIrregularWorkingHours,
      featureLeaveDaysSpecificWorkingConditions,
      featureLeaveDaysAdditionalLeave,
      featureHourlyAbsences,
      featureHealthAndSafetyTraining,
      featureWorkTrips,
      featureCustomFeatureForClientF,
      featureLimitWfhLeaveDays,
      featureShowWFHLeaveTypeInMainDropdown,
      featureLeaveSubstituteEmployee,
      featureRequireLeaveSubstituteEmployee,
      featureEmployeeCandidatesIsActive,
      featureGroupRoleLimitsIsActive,
      featureReminderForProbationPeriodEnd,
      featureLimitDocumentsToLeaves,
      reminderForIdCardExpiration,
      shouldDisallowLeaveDays,
      disallowedLeavesFrom,
      disallowedLeavesTo,
      isCandidate,
      employeeCandidateHelpText,
      totalHourlyAbsencesRemainingHours,
      totalHourlyAbsencesUncoveredHours,
      totalHourlyAbsencesHoursAboutToBeCovered,
      hasAvailableArchives,
      availableUndownloadedArchivesCount,
      inProgressArchivesCount,
      lastArchiveExpiresAt,
    });

    // Update context values if setters are provided
    if (setWorkMode) setWorkMode(workMode);
    if (setWorkModeName) setWorkModeName(workModeName);
    if (setAltWorkModeName) setAltWorkModeName(altWorkModeName);
    if (setLanguage) setLanguage(lang);

    // Call success callback if provided
    if (onSuccess) onSuccess(data);

    return data;
  } catch (error) {
    console.error('Failed to update user data:', error);

    // Call error callback if provided
    if (onError) onError(error);

    throw error;
  }
};

/**
 * Parse user data from API response (useful for login/activate endpoints that return user data directly)
 * This function handles the transformation and storage of user data
 *
 * @param data - The API response data
 * @param options - Optional callbacks for setting context values
 */
export const parseAndStoreUserData = (
  data: AuthMeResponse,
  options: Omit<UpdateUserDataOptions, 'onSuccess' | 'onError'> = {}
): void => {
  const { setWorkMode, setWorkModeName, setAltWorkModeName, setLanguage } = options;

  const {
    id,
    first_name: firstName,
    last_name: lastName,
    email,
    work_mode: workMode,
    work_mode_name: workModeName,
    alt_work_mode_name: altWorkModeName,
    lang,
    paid_leave_days: paidLeaveDays,
    paid_leave_days_by_year: paidLeaveDaysByYear,
    paid_leave_served_time_days: paidLeaveDaysServedTime,
    paid_leave_irregular_working_hours: paidLeaveDaysIrregularWorkingHours,
    paid_leave_irregular_working_hours_days: paidLeaveDaysIrregularWorkingHoursDays,
    paid_leave_specific_working_conditions: paidLeaveDaysSpecificWorkingConditions,
    paid_leave_specific_working_conditions_days: paidLeaveDaysSpecificWorkingConditionsDays,
    paid_leave_additional_days: paidLeaveDaysAdditionalLeave,
    wfh_limit_type: wfhLimitType,
    remaining_wfh_leaves_per_week: remainingWfhLeavesPerWeek,
    remaining_wfh_leaves_per_month: remainingWfhLeavesPerMonth,
    feature_leaves_is_active: featureLeavesIsActive,
    feature_leave_review_is_active: featureLeaveReview,
    feature_leaves_half_day_option_is_active: featureLeavesHalfDayOption,
    feature_form_76_is_active: featureForm76,
    feature_colleagues_leaves_is_active: featureColleaguesLeaves,
    feature_exit_interview_is_active: featureExitInterview,
    feature_sick_leave_without_attachment_is_active: featureSickLeaveWithoutAttachment,
    feature_art157_leave_without_attachment_is_active: featureArt157LeaveWithoutAttachment,
    feature_leave_days_served_time_is_active: featureLeaveDaysServedTime,
    feature_leave_days_irregular_working_hours_is_active: featureLeaveDaysIrregularWorkingHours,
    feature_leave_days_specific_working_conditions_is_active: featureLeaveDaysSpecificWorkingConditions,
    feature_paid_additional_leave_is_active: featureLeaveDaysAdditionalLeave,
    feature_hourly_absences_is_active: featureHourlyAbsences,
    feature_health_and_safety_training_is_active: featureHealthAndSafetyTraining,
    feature_work_trips_is_active: featureWorkTrips,
    feature_custom_feature_for_client_f: featureCustomFeatureForClientF,
    feature_limit_wfh_leave_days: featureLimitWfhLeaveDays,
    feature_show_wfh_leave_type_in_main_dropdown: featureShowWFHLeaveTypeInMainDropdown,
    feature_leave_substitute_employee_is_active: featureLeaveSubstituteEmployee,
    feature_require_leave_substitute_employee_is_active: featureRequireLeaveSubstituteEmployee,
    feature_employee_candidates_is_active: featureEmployeeCandidatesIsActive,
    feature_group_role_limits_is_active: featureGroupRoleLimitsIsActive,
    feature_reminder_for_probation_period_end: featureReminderForProbationPeriodEnd,
    feature_limit_documents_to_leaves: featureLimitDocumentsToLeaves,
    reminder_for_id_card_expiration: reminderForIdCardExpiration,
    should_disallow_leave_days: shouldDisallowLeaveDays,
    disallowed_leaves_from: disallowedLeavesFrom,
    disallowed_leaves_to: disallowedLeavesTo,
    is_candidate: isCandidate,
    employee_candidate_help_text: employeeCandidateHelpText,
    total_hourly_absences_remaining_hours: totalHourlyAbsencesRemainingHours,
    total_hourly_absences_uncovered_hours: totalHourlyAbsencesUncoveredHours,
    total_hourly_absences_hours_about_to_be_covered: totalHourlyAbsencesHoursAboutToBeCovered,
    has_available_archives: hasAvailableArchives,
    available_undownloaded_archives_count: availableUndownloadedArchivesCount,
    in_progress_archives_count: inProgressArchivesCount,
    last_archive_expires_at: lastArchiveExpiresAt,
  } = data;

  // Set user in error monitoring
  ErrorMonitor.setUser({ id: String(id), email });

  // Update user info in local storage
  setUserInfo({
    id,
    name: `${firstName} ${lastName}`,
    email,
    paidLeaveDays: paidLeaveDays || 0,
    paidLeaveDaysByYear: paidLeaveDaysByYear || [],
    paidLeaveDaysServedTime: paidLeaveDaysServedTime || 0,
    paidLeaveDaysIrregularWorkingHours:
      paidLeaveDaysIrregularWorkingHours || paidLeaveDaysIrregularWorkingHoursDays || 0,
    paidLeaveDaysSpecificWorkingConditions:
      paidLeaveDaysSpecificWorkingConditions || paidLeaveDaysSpecificWorkingConditionsDays || 0,
    paidLeaveDaysAdditionalLeave: paidLeaveDaysAdditionalLeave || 0,
    wfhLimitType,
    remainingWfhLeavesPerWeek: remainingWfhLeavesPerWeek || 0,
    remainingWfhLeavesPerMonth: remainingWfhLeavesPerMonth || 0,
    featureLeavesIsActive,
    featureLeaveReview,
    featureLeavesHalfDayOption,
    featureForm76,
    featureColleaguesLeaves,
    featureExitInterview,
    featureSickLeaveWithoutAttachment,
    featureArt157LeaveWithoutAttachment,
    featureLeaveDaysServedTime,
    featureLeaveDaysIrregularWorkingHours,
    featureLeaveDaysSpecificWorkingConditions,
    featureLeaveDaysAdditionalLeave,
    featureHourlyAbsences,
    featureHealthAndSafetyTraining,
    featureWorkTrips,
    featureCustomFeatureForClientF,
    featureLimitWfhLeaveDays,
    featureShowWFHLeaveTypeInMainDropdown,
    featureLeaveSubstituteEmployee,
    featureRequireLeaveSubstituteEmployee,
    featureEmployeeCandidatesIsActive,
    featureGroupRoleLimitsIsActive,
    featureReminderForProbationPeriodEnd,
    featureLimitDocumentsToLeaves,
    reminderForIdCardExpiration,
    shouldDisallowLeaveDays,
    disallowedLeavesFrom,
    disallowedLeavesTo,
    isCandidate,
    employeeCandidateHelpText,
    totalHourlyAbsencesRemainingHours,
    totalHourlyAbsencesUncoveredHours,
    totalHourlyAbsencesHoursAboutToBeCovered,
    hasAvailableArchives,
    availableUndownloadedArchivesCount,
    inProgressArchivesCount,
    lastArchiveExpiresAt,
  });

  // Update context values if setters are provided
  if (setWorkMode) setWorkMode(workMode);
  if (setWorkModeName) setWorkModeName(workModeName);
  if (setAltWorkModeName) setAltWorkModeName(altWorkModeName);
  if (setLanguage) setLanguage(lang);
};
